import type { WhatsAppSendRecipient, WhatsAppContextField } from '../../models/MasterAdmin/whatsappHub';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';
import { previewDeepLinkFromCampaign } from './whatsappDeepLinkPreview';
import { formatDisplayPhone } from './whatsappUi';
import { deduplicateDoctorPrefix } from './whatsappDoctorHonorific';

export type SendRecipientFieldKey =
  | 'name'
  | 'phone'
  | 'email'
  | 'city'
  | 'platform'
  | 'vendorKind'
  | 'recipientType'
  | 'tags';

export type SendVariableSourceType = 'static' | 'recipient_field' | 'deep_link';

export type SendTemplateVariableSlot = {
  slot: number;
  sourceType: SendVariableSourceType;
  staticValue?: string;
  /** Recipient field key or notification/context catalog key. */
  fieldKey?: string | null;
  deepLinkCampaignId?: string | null;
  deepLinkUrlType?: 'short' | 'full' | 'dynamic';
  deepLinkParamBindings?: Record<string, string>;
};

export type SendRecipientFieldDef = {
  key: SendRecipientFieldKey;
  label: string;
  /** Which recipient sources this field applies to (empty = all). */
  sources?: Array<'contact' | 'user' | 'vendor'>;
};

const BASE_FIELDS: SendRecipientFieldDef[] = [
  { key: 'name', label: 'Name' },
  { key: 'phone', label: 'Phone number' },
  { key: 'email', label: 'Email' },
  { key: 'city', label: 'City' },
];

const USER_FIELDS: SendRecipientFieldDef[] = [{ key: 'platform', label: 'App platform', sources: ['user'] }];

const VENDOR_FIELDS: SendRecipientFieldDef[] = [
  { key: 'vendorKind', label: 'Vendor service (Doctor / Ambulance)', sources: ['vendor'] },
];

const CONTACT_FIELDS: SendRecipientFieldDef[] = [
  { key: 'tags', label: 'Contact tags', sources: ['contact'] },
  { key: 'recipientType', label: 'Linked profile type', sources: ['contact'] },
];

const RECIPIENT_ONLY_KEYS = new Set<string>(['vendorKind', 'tags', 'recipientType']);

/** Map notification/template context keys to send recipient fields when possible. */
const CONTEXT_KEY_TO_RECIPIENT_FIELD: Partial<Record<string, SendRecipientFieldKey>> = {
  name: 'name',
  patientName: 'name',
  userName: 'name',
  customerName: 'name',
  doctorName: 'name',
  vendorName: 'name',
  driverName: 'name',
  email: 'email',
  city: 'city',
  platform: 'platform',
};

function recipientSourceKind(r: WhatsAppSendRecipient): 'contact' | 'user' | 'vendor' {
  const type = String(r.recipientType || r.source || '').toLowerCase();
  if (type === 'user') return 'user';
  if (type === 'vendor') return 'vendor';
  return 'contact';
}

export function getSendRecipientFields(recipients: WhatsAppSendRecipient[]): SendRecipientFieldDef[] {
  const kinds = new Set(recipients.map(recipientSourceKind));
  if (!kinds.size) kinds.add('contact');

  const all = [...BASE_FIELDS, ...USER_FIELDS, ...VENDOR_FIELDS, ...CONTACT_FIELDS];
  return all.filter((field) => {
    if (!field.sources?.length) return true;
    return field.sources.some((s) => kinds.has(s));
  });
}

/** Context keys for deep link route param bindings on manual send. */
export function getSendDeepLinkContextFields(recipients: WhatsAppSendRecipient[]): WhatsAppContextField[] {
  const kinds = new Set(recipients.map(recipientSourceKind));
  if (!kinds.size) kinds.add('contact');

  const fields: WhatsAppContextField[] = [
    { key: 'patientName', label: 'Recipient name', sourceType: 'send_recipient' },
    { key: 'userName', label: 'Recipient name', sourceType: 'send_recipient' },
    { key: 'customerName', label: 'Recipient name', sourceType: 'send_recipient' },
    { key: 'doctorName', label: 'Recipient name', sourceType: 'send_recipient' },
    { key: 'vendorName', label: 'Recipient name', sourceType: 'send_recipient' },
    { key: 'name', label: 'Recipient name', sourceType: 'send_recipient' },
    { key: 'email', label: 'Email', sourceType: 'send_recipient' },
    { key: 'city', label: 'City', sourceType: 'send_recipient' },
    { key: 'phone', label: 'Phone number', sourceType: 'send_recipient' },
    { key: 'platform', label: 'App platform', sourceType: 'send_recipient' },
  ];

  if (kinds.has('user')) {
    fields.push({ key: 'userId', label: 'User ID', sourceType: 'send_recipient' });
  }
  if (kinds.has('vendor')) {
    fields.push({ key: 'vendorId', label: 'Vendor ID', sourceType: 'send_recipient' });
  }
  fields.push({ key: 'recipientId', label: 'Profile ID (user or vendor)', sourceType: 'send_recipient' });

  return fields;
}

type DeepLinkTokenPayload = {
  c: string;
  t?: 'short' | 'full' | 'dynamic';
  p?: Record<string, string>;
};

export function encodeDeepLinkToken(payload: DeepLinkTokenPayload): string {
  const json = JSON.stringify(payload);
  const b64 =
    typeof btoa !== 'undefined'
      ? btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
      : json;
  return `@deeplink:${b64}`;
}

export function parseDeepLinkToken(value: string): DeepLinkTokenPayload | null {
  const match = String(value || '')
    .trim()
    .match(/^@deeplink:(.+)$/i);
  if (!match) return null;
  try {
    const raw = match[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = raw + '='.repeat((4 - (raw.length % 4)) % 4);
    const json =
      typeof atob !== 'undefined' ? atob(padded) : raw;
    const parsed = JSON.parse(json) as DeepLinkTokenPayload;
    if (!parsed?.c) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function isDeepLinkToken(value: string): boolean {
  return /^@deeplink:/i.test(String(value || '').trim());
}

export function recipientFieldToken(fieldKey: string): string {
  return `@recipient:${fieldKey}`;
}

export function contextFieldToken(fieldKey: string): string {
  return `@context:${fieldKey}`;
}

export function isRecipientFieldToken(value: string): boolean {
  return /^@recipient:[a-z0-9_]+$/i.test(String(value || '').trim());
}

export function parseRecipientFieldToken(value: string): string | null {
  const match = String(value || '')
    .trim()
    .match(/^@recipient:([a-z0-9_]+)$/i);
  return match?.[1] || null;
}

export function parseContextFieldToken(value: string): string | null {
  const match = String(value || '')
    .trim()
    .match(/^@context:([a-z0-9_]+)$/i);
  return match?.[1] || null;
}

function formatPlatformLabel(platform?: string | null): string {
  if (!platform) return '';
  const p = platform.toLowerCase();
  if (p === 'ios') return 'iOS';
  if (p === 'android') return 'Android';
  if (p === 'web') return 'Web';
  return platform;
}

function formatVendorKindLabel(kind?: string | null): string {
  if (!kind) return '';
  const k = kind.toLowerCase();
  if (k.includes('doctor') || k === 'doctor-clinic') return 'Doctor';
  if (k.includes('ambulance')) return 'Ambulance';
  return kind;
}

export function resolveSendRecipientFieldValue(
  fieldKey: string,
  recipient: WhatsAppSendRecipient
): string {
  const mapped = CONTEXT_KEY_TO_RECIPIENT_FIELD[fieldKey] || fieldKey;
  switch (mapped) {
    case 'name':
      return recipient.displayName?.trim() || 'Customer';
    case 'phone':
      return formatDisplayPhone(recipient.countryCode, recipient.phoneNumber) || recipient.maskedPhone || '';
    case 'email':
      return recipient.email?.trim() || '';
    case 'city':
      return recipient.city?.trim() || '';
    case 'platform':
      return formatPlatformLabel(recipient.platform);
    case 'vendorKind':
      return formatVendorKindLabel(recipient.vendorKind);
    case 'recipientType':
      return recipient.recipientType?.trim() || '';
    case 'tags':
      return (recipient.tags || []).filter(Boolean).join(', ');
    default:
      return '';
  }
}

export function sendSlotToBodyValue(slot: SendTemplateVariableSlot): string {
  if (slot.sourceType === 'recipient_field' && slot.fieldKey) {
    const mapped = CONTEXT_KEY_TO_RECIPIENT_FIELD[slot.fieldKey];
    if (mapped) return recipientFieldToken(mapped);
    if (RECIPIENT_ONLY_KEYS.has(slot.fieldKey)) return recipientFieldToken(slot.fieldKey);
    return contextFieldToken(slot.fieldKey);
  }
  if (slot.sourceType === 'deep_link' && slot.deepLinkCampaignId) {
    return encodeDeepLinkToken({
      c: slot.deepLinkCampaignId,
      t: slot.deepLinkUrlType || 'short',
      p: slot.deepLinkParamBindings || {},
    });
  }
  return slot.staticValue ?? '';
}

export function bodyValueToSendSlot(slot: number, value: string): SendTemplateVariableSlot {
  const deepLink = parseDeepLinkToken(value);
  if (deepLink) {
    return {
      slot,
      sourceType: 'deep_link',
      deepLinkCampaignId: deepLink.c,
      deepLinkUrlType: deepLink.t || 'short',
      deepLinkParamBindings: deepLink.p || {},
    };
  }
  const contextKey = parseContextFieldToken(value);
  if (contextKey) {
    return { slot, sourceType: 'recipient_field', fieldKey: contextKey };
  }
  const fieldKey = parseRecipientFieldToken(value);
  if (fieldKey) {
    return { slot, sourceType: 'recipient_field', fieldKey };
  }
  return { slot, sourceType: 'static', staticValue: value };
}

export function defaultSendSlots(
  variableCount: number,
  templateDefaultMappings?: Array<{
    slot: number;
    sourceType?: string;
    contextKey?: string | null;
    staticValue?: string;
    deepLinkCampaignId?: string | null;
    deepLinkUrlType?: 'short' | 'full' | 'dynamic';
    deepLinkParamBindings?: Record<string, string>;
  }>
): SendTemplateVariableSlot[] {
  const count = Math.max(variableCount, 0);
  return Array.from({ length: count }, (_, i) => {
    const slot = i + 1;
    const mapping = templateDefaultMappings?.find((m) => m.slot === slot);
    if (mapping?.sourceType === 'deep_link' && mapping.deepLinkCampaignId) {
      return {
        slot,
        sourceType: 'deep_link',
        deepLinkCampaignId: mapping.deepLinkCampaignId,
        deepLinkUrlType: mapping.deepLinkUrlType || 'short',
        deepLinkParamBindings: mapping.deepLinkParamBindings || {},
      };
    }
    if (mapping?.sourceType === 'static' && mapping.staticValue?.trim()) {
      return { slot, sourceType: 'static', staticValue: mapping.staticValue.trim() };
    }
    if (mapping?.sourceType === 'context' && mapping.contextKey) {
      return { slot, sourceType: 'recipient_field', fieldKey: mapping.contextKey };
    }
    if (slot === 1) {
      return { slot, sourceType: 'recipient_field', fieldKey: 'name' };
    }
    return { slot, sourceType: 'static', staticValue: '' };
  });
}

export function buildSendPreviewContext(recipient: WhatsAppSendRecipient): Record<string, string> {
  const name = resolveSendRecipientFieldValue('name', recipient);
  const type = String(recipient.recipientType || '').toLowerCase();
  return {
    patientName: name,
    userName: name,
    customerName: name,
    doctorName: name,
    vendorName: name,
    name,
    email: resolveSendRecipientFieldValue('email', recipient),
    city: resolveSendRecipientFieldValue('city', recipient),
    phone: resolveSendRecipientFieldValue('phone', recipient),
    platform: resolveSendRecipientFieldValue('platform', recipient),
    userId: type === 'user' ? String(recipient.recipientId || '') : '',
    vendorId: type === 'vendor' ? String(recipient.recipientId || '') : '',
    recipientId: String(recipient.recipientId || ''),
  };
}

export function resolveSendPreviewValues(
  slots: SendTemplateVariableSlot[],
  recipient: WhatsAppSendRecipient | undefined,
  campaignsById?: Map<string, DeepLinkCampaign>
): string[] {
  return slots.map((slot) => {
    if (slot.sourceType === 'deep_link' && slot.deepLinkCampaignId) {
      const url =
        campaignsById &&
        previewDeepLinkFromCampaign(
          {
            deepLinkCampaignId: slot.deepLinkCampaignId,
            deepLinkUrlType: slot.deepLinkUrlType,
            deepLinkParamBindings: slot.deepLinkParamBindings,
          },
          campaignsById
        );
      return url || 'https://vedika.health/l/example';
    }
    if (slot.sourceType === 'recipient_field' && slot.fieldKey) {
      const val = recipient ? resolveSendRecipientFieldValue(slot.fieldKey, recipient) : '';
      if (val) return deduplicateDoctorPrefix(val);
      const ctx = recipient ? buildSendPreviewContext(recipient) : {};
      return deduplicateDoctorPrefix(ctx[slot.fieldKey] || `Sample ${slot.fieldKey}`);
    }
    return deduplicateDoctorPrefix(slot.staticValue?.trim() || `Sample ${slot.slot}`);
  });
}

export function sendSlotsAreValid(slots: SendTemplateVariableSlot[]): boolean {
  return slots.every((slot) => {
    if (slot.sourceType === 'recipient_field') return Boolean(slot.fieldKey);
    if (slot.sourceType === 'deep_link') return Boolean(slot.deepLinkCampaignId?.trim());
    return (slot.staticValue ?? '').trim().length > 0;
  });
}
